/**
 * Prompt Test Playground
 * Fill in variables, preview resolved prompt, and execute against OpenAI/OpenRouter.
 * Supports presets and model selection.
 */

import { useState, useEffect, useMemo } from 'react';
import apiClient from '../../lib/api';
import { toast } from 'sonner';

interface Preset {
  name: string;
  values: Record<string, string>;
}

interface ModelOption {
  id: string;
  name: string;
  provider: string;
  pricing?: { prompt: number; completion: number }; // per 1M tokens USD
  contextLength?: number;
}

interface PromptTestPlaygroundProps {
  promptKey: string;
  variables: string[];
  variableDefaults?: Record<string, string>;
  content?: string;
}

const PRESETS_STORAGE_KEY = 'growe:prompt-presets';
const MODEL_STORAGE_KEY = 'growe:prompt-test-model';

function formatPrice(price: number): string {
  if (price === 0) return 'gratis';
  if (price < 0.01) return '<$0.01';
  if (price < 1) return `$${price.toFixed(2)}`;
  return `$${price.toFixed(2)}`;
}

function formatPricing(m: ModelOption): string {
  if (!m.pricing) return '';
  return `${formatPrice(m.pricing.prompt)} / ${formatPrice(m.pricing.completion)}`;
}

function loadPresets(promptKey: string): Preset[] {
  try {
    const all = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}');
    return all[promptKey] || [];
  } catch {
    return [];
  }
}

function savePresets(promptKey: string, presets: Preset[]) {
  try {
    const all = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}');
    all[promptKey] = presets;
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(all));
  } catch {}
}

export default function PromptTestPlayground({
  promptKey,
  variables,
  variableDefaults,
  content,
}: PromptTestPlaygroundProps) {
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    Object.fromEntries(variables.map((v) => [v, variableDefaults?.[v] || '']))
  );
  const [result, setResult] = useState<{
    resolvedPrompt: string;
    tokenEstimate: number;
    aiResponse?: string;
    usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  } | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isExecuteLoading, setIsExecuteLoading] = useState(false);

  // Model selection
  const [models, setModels] = useState<ModelOption[]>([]);
  const [selectedModel, setSelectedModel] = useState(() =>
    localStorage.getItem(MODEL_STORAGE_KEY) || 'gpt-4o-mini'
  );
  const [modelSearch, setModelSearch] = useState('');
  const [showModelDropdown, setShowModelDropdown] = useState(false);

  // Presets
  const [presets, setPresets] = useState<Preset[]>(() => loadPresets(promptKey));
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');

  // Fetch models on mount
  useEffect(() => {
    apiClient.get('/admin/prompts/models').then((res) => {
      setModels(res.data.models || []);
    }).catch(() => {
      // Fallback to basic OpenAI models
      setModels([
        { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI' },
        { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'OpenAI' },
      ]);
    });
  }, []);

  useEffect(() => {
    setPresets(loadPresets(promptKey));
    setActivePreset(null);
  }, [promptKey]);

  // Save selected model to localStorage
  useEffect(() => {
    localStorage.setItem(MODEL_STORAGE_KEY, selectedModel);
  }, [selectedModel]);

  // Filtered models for search
  const filteredModels = useMemo(() => {
    if (!modelSearch) return models;
    const q = modelSearch.toLowerCase();
    return models.filter(
      (m) => m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q)
    );
  }, [models, modelSearch]);

  // Group models by provider
  const groupedModels = useMemo(() => {
    const groups: Record<string, ModelOption[]> = {};
    for (const m of filteredModels) {
      if (!groups[m.provider]) groups[m.provider] = [];
      groups[m.provider].push(m);
    }
    return groups;
  }, [filteredModels]);

  const selectedModelObj = models.find((m) => m.id === selectedModel);
  const selectedModelName = selectedModelObj?.name || selectedModel;

  const handleSavePreset = () => {
    if (!newPresetName.trim()) return;
    const name = newPresetName.trim();
    const updated = [...presets.filter((p) => p.name !== name), { name, values: { ...variableValues } }];
    setPresets(updated);
    savePresets(promptKey, updated);
    setActivePreset(name);
    setNewPresetName('');
    setShowSavePreset(false);
    toast.success(`Preset "${name}" opgeslagen`);
  };

  const handleLoadPreset = (preset: Preset) => {
    setVariableValues(Object.fromEntries(variables.map((v) => [v, preset.values[v] || ''])));
    setActivePreset(preset.name);
    setResult(null);
  };

  const handleDeletePreset = (name: string) => {
    const updated = presets.filter((p) => p.name !== name);
    setPresets(updated);
    savePresets(promptKey, updated);
    if (activePreset === name) setActivePreset(null);
  };

  const handleLoadDefaults = () => {
    setVariableValues(Object.fromEntries(variables.map((v) => [v, variableDefaults?.[v] || ''])));
    setActivePreset(null);
    setResult(null);
  };

  const handlePreview = async () => {
    setIsPreviewLoading(true);
    try {
      const response = await apiClient.post(
        `/admin/prompts/${encodeURIComponent(promptKey)}/test`,
        { content, variables: variableValues }
      );
      setResult(response.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Preview mislukt');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleExecute = async () => {
    setIsExecuteLoading(true);
    try {
      const response = await apiClient.post(
        `/admin/prompts/${encodeURIComponent(promptKey)}/test`,
        {
          content,
          variables: variableValues,
          model: selectedModel,
          execute: true,
        }
      );
      setResult(response.data);
      if (response.data.usage) {
        toast.success(`AI response ontvangen (${response.data.usage.totalTokens} tokens)`);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Uitvoeren mislukt');
    } finally {
      setIsExecuteLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-black dark:text-white">Test Playground</h3>
        {variables.length > 0 && (
          <button
            onClick={() => setShowSavePreset(!showSavePreset)}
            className="px-3 py-1 text-xs font-bold border-2 border-black dark:border-gray-600 bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            + Preset opslaan
          </button>
        )}
      </div>

      {/* Model selector */}
      <div className="relative">
        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">
          Model
        </label>
        <button
          onClick={() => setShowModelDropdown(!showModelDropdown)}
          className="w-full px-3 py-2 text-sm text-left border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] flex items-center justify-between"
        >
          <span className="truncate">{selectedModelName}</span>
          <span className="flex items-center gap-3 ml-2 shrink-0">
            {selectedModelObj?.pricing && (
              <span className="text-xs text-gray-500">{formatPricing(selectedModelObj)} /1M</span>
            )}
            <span className="text-xs text-gray-400">{selectedModel.includes('/') ? 'OpenRouter' : 'OpenAI'}</span>
          </span>
        </button>

        {showModelDropdown && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => { setShowModelDropdown(false); setModelSearch(''); }}
            />
            <div className="absolute z-20 top-full left-0 right-0 mt-1 border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-h-80 flex flex-col">
              <input
                type="text"
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                placeholder="Zoek model..."
                className="w-full px-3 py-2 text-sm border-b-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none"
                autoFocus
              />
              <div className="overflow-y-auto flex-1">
                {Object.entries(groupedModels).map(([provider, providerModels]) => (
                  <div key={provider}>
                    <div className="px-3 py-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 sticky top-0">
                      {provider} ({providerModels.length})
                    </div>
                    {providerModels.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setSelectedModel(m.id);
                          setShowModelDropdown(false);
                          setModelSearch('');
                        }}
                        className={`w-full px-3 py-1.5 text-sm text-left font-mono hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                          selectedModel === m.id ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black dark:text-white'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{m.name}</span>
                          {m.pricing && (
                            <span className={`text-xs shrink-0 ${
                              selectedModel === m.id ? 'opacity-70' : 'text-gray-400'
                            }`}>
                              {formatPricing(m)}
                            </span>
                          )}
                        </div>
                        {m.name !== m.id && (
                          <div className={`text-xs truncate ${
                            selectedModel === m.id ? 'opacity-60' : 'text-gray-400'
                          }`}>{m.id}</div>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
                {filteredModels.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    Geen modellen gevonden
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Preset bar */}
      {variables.length > 0 && (presets.length > 0 || showSavePreset) && (
        <div className="space-y-2">
          {presets.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-bold">Presets:</span>
              <button
                onClick={handleLoadDefaults}
                className={`px-3 py-1 text-xs font-mono border-2 transition-colors ${
                  activePreset === null
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white'
                }`}
              >
                Defaults
              </button>
              {presets.map((preset) => (
                <div key={preset.name} className="flex items-center">
                  <button
                    onClick={() => handleLoadPreset(preset)}
                    className={`px-3 py-1 text-xs font-mono border-2 border-r-0 transition-colors ${
                      activePreset === preset.name
                        ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white'
                    }`}
                  >
                    {preset.name}
                  </button>
                  <button
                    onClick={() => handleDeletePreset(preset.name)}
                    className="px-1.5 py-1 text-xs border-2 border-gray-300 dark:border-gray-600 text-gray-400 hover:text-red-600 hover:border-red-600 transition-colors"
                    title="Verwijder preset"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}

          {showSavePreset && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                placeholder="Preset naam (bijv. 'SaaS klant', 'E-commerce')"
                className="flex-1 px-3 py-1.5 text-xs border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none"
                autoFocus
              />
              <button
                onClick={handleSavePreset}
                disabled={!newPresetName.trim()}
                className="px-3 py-1.5 text-xs font-bold border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black disabled:opacity-50"
              >
                Opslaan
              </button>
              <button
                onClick={() => { setShowSavePreset(false); setNewPresetName(''); }}
                className="px-3 py-1.5 text-xs border-2 border-gray-300 dark:border-gray-600 text-gray-500"
              >
                Annuleer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Variable inputs */}
      {variables.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {variables.map((v) => (
            <div key={v}>
              <label className="block text-xs font-mono text-gray-600 dark:text-gray-400 mb-1">
                {`{{${v}}}`}
              </label>
              <textarea
                value={variableValues[v] || ''}
                onChange={(e) =>
                  setVariableValues((prev) => ({ ...prev, [v]: e.target.value }))
                }
                className="w-full px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] resize-y"
                rows={2}
                placeholder={`Waarde voor ${v}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={handlePreview}
          disabled={isPreviewLoading || isExecuteLoading}
          className="px-4 py-2 text-sm font-bold border-2 border-black dark:border-gray-600 bg-white dark:bg-gray-800 text-black dark:text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(75,75,75,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
        >
          {isPreviewLoading ? 'Laden...' : 'Preview'}
        </button>
        <button
          onClick={handleExecute}
          disabled={isPreviewLoading || isExecuteLoading}
          className="px-4 py-2 text-sm font-bold border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
        >
          {isExecuteLoading ? 'AI draait...' : 'Uitvoeren (AI call)'}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Resolved prompt</span>
              <span className="font-mono">~{result.tokenEstimate} tokens</span>
            </div>
            <pre className="p-4 border-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm font-mono text-black dark:text-white whitespace-pre-wrap overflow-auto max-h-64">
              {result.resolvedPrompt}
            </pre>
          </div>

          {result.aiResponse && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="font-bold text-green-700 dark:text-green-400">AI Output ({selectedModelName})</span>
                {result.usage && (
                  <span className="font-mono flex items-center gap-3">
                    <span>{result.usage.promptTokens} in / {result.usage.completionTokens} out</span>
                    {selectedModelObj?.pricing && (
                      <span className="text-orange-600 dark:text-orange-400 font-bold">
                        ~${(
                          (result.usage.promptTokens * selectedModelObj.pricing.prompt / 1000000) +
                          (result.usage.completionTokens * selectedModelObj.pricing.completion / 1000000)
                        ).toFixed(4)}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <pre className="p-4 border-2 border-green-600 dark:border-green-500 bg-green-50 dark:bg-green-950 text-sm font-mono text-black dark:text-white whitespace-pre-wrap overflow-auto max-h-96">
                {result.aiResponse}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
